import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EyeOff, Heart, Loader2, Sparkles, ExternalLink } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { track, affiliateProps } from "@/lib/posthog";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useIsHueloTeam } from "@/hooks/useIsHueloTeam";
import { useToast } from "@/hooks/use-toast";
import { fetchFeed, setLike, setPostHidden, type FeedPost } from "@/lib/feed";
import { feedTitle } from "@/lib/feedTitle";

type Filter = "all" | "liked";

/** One product per furniture piece — the design stores several alternatives per piece. */
const shopItems = (products: FeedPost["products"]) => {
  const seen = new Set<string>();
  return products.filter((p) => {
    const key = (p.furniture?.item || p.title).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const Feed = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isTeam = useIsHueloTeam();
  const { addItem, removeItem, isItemSaved } = useSavedItems();
  const [userId, setUserId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [open, setOpen] = useState<FeedPost | null>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setUserId(data.session?.user?.id ?? null));
    track("feed_viewed");
  }, []);

  const load = useCallback(
    async (reset: boolean) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      try {
        const before = reset ? null : posts[posts.length - 1]?.created_at ?? null;
        const page = await fetchFeed(before, filter === "liked");
        setPosts((prev) => (reset ? page : [...prev, ...page]));
        setHasMore(page.length > 0 && page.length >= 24);
      } catch {
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [posts, filter],
  );

  useEffect(() => {
    setPosts([]);
    setHasMore(true);
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasMore && !loadingRef.current && posts.length > 0) void load(false);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, posts.length, load]);

  const updatePost = (id: string, patch: Partial<FeedPost>) => {
    setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    setOpen((o) => (o && o.id === id ? { ...o, ...patch } : o));
  };

  const toggleLike = async (post: FeedPost) => {
    if (!userId) return;
    const liked = !post.liked_by_me;
    updatePost(post.id, { liked_by_me: liked, like_count: post.like_count + (liked ? 1 : -1) });
    track("feed_liked", { post_id: post.id, liked, style: post.design_style ?? undefined });
    try {
      await setLike(post.id, userId, liked);
    } catch {
      updatePost(post.id, { liked_by_me: post.liked_by_me, like_count: post.like_count });
    }
  };

  const hide = async (post: FeedPost) => {
    try {
      await setPostHidden(post.id, true);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      setOpen(null);
      toast({ title: "Hidden from feed" });
    } catch {
      toast({ title: "Couldn't hide post", variant: "destructive" });
    }
  };

  const startWithStyle = (post: FeedPost) => {
    track("feed_use_style_clicked", { post_id: post.id, style: post.design_style ?? undefined });
    navigate(post.design_style ? `/app?style=${encodeURIComponent(post.design_style)}` : "/app");
  };

  const openPost = (post: FeedPost) => {
    setOpen(post);
    track("feed_post_opened", { post_id: post.id, style: post.design_style ?? undefined });
  };

  return (
    <AppShell>
      <main className="flex-1 px-4 sm:px-6 lg:px-10 py-8 max-w-6xl w-full mx-auto">
        <header className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground">Inspiration</h1>
            <p className="text-sm text-muted-foreground mt-1">Rooms redesigned by the Huelo community.</p>
          </div>
          <div className="inline-flex rounded-full bg-muted p-1 self-start">
            {(["all", "liked"] as Filter[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  filter === f ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {f === "all" ? "All" : "Liked"}
              </button>
            ))}
          </div>
        </header>

        {!loading && posts.length === 0 ? (
          <div className="text-center py-20 space-y-3">
            <p className="text-sm text-muted-foreground">
              {filter === "liked" ? "You haven't liked any designs yet." : "No shared designs yet. Be the first!"}
            </p>
            {filter === "all" && (
              <button
                onClick={() => navigate("/dashboard")}
                className="text-xs font-semibold underline underline-offset-2 text-foreground"
              >
                Share one of your designs
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {posts.map((post) => (
              <article
                key={post.id}
                className="group rounded-xl overflow-hidden bg-card border border-border shadow-sm hover:shadow-md transition-shadow"
              >
                <button onClick={() => openPost(post)} className="block w-full" aria-label="Open design">
                  <img
                    src={post.image_url}
                    alt={feedTitle(post)}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover transition-transform group-hover:scale-[1.02]"
                  />
                </button>
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm font-semibold text-foreground min-w-0 pr-2">{feedTitle(post)}</span>
                  <button
                    onClick={() => toggleLike(post)}
                    aria-label={post.liked_by_me ? "Unlike" : "Like"}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <Heart className={`w-4 h-4 ${post.liked_by_me ? "fill-primary text-primary" : ""}`} />
                    {post.like_count}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        <div ref={sentinel} className="h-10" />
        {loading && (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}
      </main>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden max-h-[92dvh] overflow-y-auto">
          {open && (
            <div>
              <img src={open.image_url} alt={feedTitle(open)} className="w-full aspect-[4/3] object-cover" />
              <div className="p-5 space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <DialogTitle className="text-lg font-semibold">{feedTitle(open)}</DialogTitle>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleLike(open)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold hover:bg-muted"
                    >
                      <Heart className={`w-4 h-4 ${open.liked_by_me ? "fill-primary text-primary" : ""}`} />
                      {open.liked_by_me ? "Liked" : "Like"} · {open.like_count}
                    </button>
                    <button
                      onClick={() => startWithStyle(open)}
                      className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold hover:opacity-90"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Use this style
                    </button>
                  </div>
                </div>

                {shopItems(open.products).length > 0 && (
                  <section>
                    <h2 className="text-sm font-semibold text-foreground mb-3">Shop this look</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {shopItems(open.products).map((p, i) => {
                        const saved = isItemSaved(p);
                        const href = p.link || p.productLink || p.searchLink;
                        return (
                          <div key={`${p.title}-${i}`} className="rounded-lg border border-border overflow-hidden flex flex-col">
                            {p.thumbnail && (
                              <img src={p.thumbnail} alt={p.title} loading="lazy" className="w-full aspect-square object-contain bg-muted" />
                            )}
                            <div className="p-2.5 flex-1 flex flex-col gap-1">
                              <p className="text-xs font-medium text-foreground line-clamp-2">{p.title}</p>
                              <p className="text-xs text-muted-foreground">{p.price} · {p.source}</p>
                              <div className="mt-auto pt-2 flex items-center justify-between gap-2">
                                {href && (
                                  <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer sponsored"
                                    onClick={() =>
                                      track("feed_product_clicked", { post_id: open.id, source: "feed", item_brand: p.source, ...affiliateProps(href) })
                                    }
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-foreground underline underline-offset-2"
                                  >
                                    View <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                                <button
                                  onClick={() => (saved ? removeItem(p) : addItem({ ...p, designId: null }, { source: "feed" }))}
                                  aria-label={saved ? "Remove from saved" : "Save item"}
                                >
                                  <Heart className={`w-4 h-4 ${saved ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-3">
                      Some links are affiliate links — Huelo may earn a commission at no extra cost to you.
                    </p>
                  </section>
                )}

                {isTeam && !open.is_mine && (
                  <button
                    onClick={() => hide(open)}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive"
                  >
                    <EyeOff className="w-3.5 h-3.5" /> Hide from feed (team)
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default Feed;
